import { useTranslation } from 'react-i18next';

export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  structuredData?: object;
  noIndex?: boolean;
}

export const getSEOData = (pageKey: string): SEOData => {
  const { t } = useTranslation();
  
  const seoData: Record<string, SEOData> = {
    home: {
      title: t('seo.home.title'),
      description: t('seo.home.description'),
      keywords: t('seo.home.keywords'),
      canonicalUrl: '/',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Workio",
        "url": "https://workio.bg",
        "description": t('seo.home.description'),
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://workio.bg/find-work?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    },
    findWork: {
      title: t('seo.findWork.title'),
      description: t('seo.findWork.description'),
      keywords: t('seo.findWork.keywords'),
      canonicalUrl: '/find-work',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": t('seo.findWork.title'),
        "description": t('seo.findWork.description'),
        "url": "https://workio.bg/find-work"
      }
    },
    findTalent: {
      title: t('seo.findTalent.title'),
      description: t('seo.findTalent.description'),
      keywords: t('seo.findTalent.keywords'),
      canonicalUrl: '/find-talent',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": t('seo.findTalent.title'),
        "description": t('seo.findTalent.description'),
        "url": "https://workio.bg/find-talent"
      }
    },
    findServices: {
      title: t('seo.findServices.title'),
      description: t('seo.findServices.description'),
      keywords: t('seo.findServices.keywords'),
      canonicalUrl: '/find-services',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": t('seo.findServices.title'),
        "description": t('seo.findServices.description'),
        "url": "https://workio.bg/find-services"
      }
    },
    login: {
      title: t('seo.login.title'),
      description: t('seo.login.description'),
      keywords: t('seo.login.keywords'),
      canonicalUrl: '/login',
      ogType: 'website'
    },
    register: {
      title: t('seo.register.title'),
      description: t('seo.register.description'),
      keywords: t('seo.register.keywords'),
      canonicalUrl: '/register',
      ogType: 'website'
    },
    about: {
      title: t('seo.about.title'),
      description: t('seo.about.description'),
      keywords: t('seo.about.keywords'),
      canonicalUrl: '/about',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": t('seo.about.title'),
        "description": t('seo.about.description'),
        "url": "https://workio.bg/about"
      }
    },
    contact: {
      title: t('seo.contact.title'),
      description: t('seo.contact.description'),
      keywords: t('seo.contact.keywords'),
      canonicalUrl: '/contact',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": t('seo.contact.title'),
        "description": t('seo.contact.description'),
        "url": "https://workio.bg/contact"
      }
    },
    privacy: {
      title: t('seo.privacy.title'),
      description: t('seo.privacy.description'),
      keywords: t('seo.privacy.keywords'),
      canonicalUrl: '/privacy',
      ogType: 'website'
    },
    terms: {
      title: t('seo.terms.title'),
      description: t('seo.terms.description'),
      keywords: t('seo.terms.keywords'),
      canonicalUrl: '/terms',
      ogType: 'website'
    },
    faq: {
      title: t('seo.faq.title'),
      description: t('seo.faq.description'),
      keywords: t('seo.faq.keywords'),
      canonicalUrl: '/faq',
      ogType: 'website',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "name": t('seo.faq.title'),
        "description": t('seo.faq.description'),
        "url": "https://workio.bg/faq"
      }
    },
    profile: {
      title: t('seo.profile.title'),
      description: t('seo.profile.description'),
      keywords: t('seo.profile.keywords'),
      canonicalUrl: '/profile',
      ogType: 'profile',
      noIndex: true
    },
    newProject: {
      title: t('seo.newProject.title'),
      description: t('seo.newProject.description'),
      keywords: t('seo.newProject.keywords'),
      canonicalUrl: '/new-project',
      ogType: 'website',
      noIndex: true
    },
    newService: {
      title: t('seo.newService.title'),
      description: t('seo.newService.description'),
      keywords: t('seo.newService.keywords'),
      canonicalUrl: '/new-service',
      ogType: 'website',
      noIndex: true
    },
    credits: {
      title: t('seo.credits.title'),
      description: t('seo.credits.description'),
      keywords: t('seo.credits.keywords'),
      canonicalUrl: '/credits',
      ogType: 'website',
      noIndex: true
    }
  };

  return seoData[pageKey] || seoData.home;
};
